/**
 * TrialFeaturesCard — настройка набора функций 30-дневного триала
 * (GET/PATCH /admin/trial-features): каталог прав по группам с тумблерами.
 * Изменения копятся локально, «Сохранить N» шлёт пакетный PATCH —
 * применяется сразу у всех триальных клиник, действие видно в аудите.
 */

import {useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {FlaskConical} from 'lucide-react';

import {getTrialFeatures, updateTrialFeatures} from '@services/trial';
import {keys} from '@queries/keys';
import {Button} from '@components/ui/Button';
import {Loader} from '@components/ui/Loader';
import {cn} from '@lib/utils';

import {TariffSection} from '../TariffSection';

const GROUP_LABEL: Record<string, string> = {
  schedule: 'Расписание',
  patients: 'Пациенты',
  cash: 'Касса',
  reports: 'Отчёты',
  staff: 'Сотрудники',
  services: 'Прайс',
};

type Props = {onSaved: (message: string) => void};

export const TrialFeaturesCard = ({onSaved}: Props) => {
  const queryClient = useQueryClient();
  const {data, isLoading} = useQuery({
    queryKey: keys.trial.features(),
    queryFn: getTrialFeatures,
    staleTime: 60_000,
  });
  /** Локальные переключения поверх данных: key → новое значение */
  const [draft, setDraft] = useState<Record<string, boolean>>({});

  const mutation = useMutation({
    mutationFn: updateTrialFeatures,
    onSuccess: (_, input) => {
      void queryClient.invalidateQueries({queryKey: keys.trial.all});
      setDraft({});
      onSaved(`Триал обновлён · изменено прав: ${input.items.length}`);
    },
  });

  const features = data ?? [];
  const valueOf = (key: string, server: boolean): boolean => draft[key] ?? server;
  const changed = features.filter((f) => draft[f.key] !== undefined && draft[f.key] !== f.inTrial);

  const handleToggle = (key: string, server: boolean) => {
    setDraft((d) => ({...d, [key]: !valueOf(key, server)}));
  };
  const handleSave = () => {
    mutation.mutate({items: changed.map((f) => ({key: f.key, inTrial: draft[f.key] === true}))});
  };

  const groups = [...new Set(features.map((f) => f.groupKey))];

  return (
    <TariffSection
      icon={<FlaskConical className="size-[23px]" strokeWidth={1.7} />}
      iconClassName="bg-skr-info-chip text-primary"
      title="Триал-функции"
      subtitle="Что доступно клиникам на 30-дневном триале · применяется сразу"
      action={
        changed.length > 0 ? (
          <Button
            size="sm"
            className="h-9 px-3.5"
            disabled={mutation.isPending}
            onClick={handleSave}
          >
            {mutation.isPending ? 'Сохраняем…' : `Сохранить · ${changed.length}`}
          </Button>
        ) : undefined
      }
    >
      {isLoading ? (
        <div className="flex min-h-[160px] items-center justify-center">
          <Loader label="Загружаем права…" />
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-x-[26px] gap-y-5 max-md:grid-cols-1">
          {groups.map((g) => (
            <div key={g}>
              <div className="mb-1.5 text-[11.5px] font-semibold uppercase tracking-[.05em] text-skr-label">
                {GROUP_LABEL[g] ?? g}
              </div>
              <div className="overflow-hidden rounded-[11px] border border-skr-line">
                {features
                  .filter((f) => f.groupKey === g)
                  .map((f) => (
                    <FeatureRow
                      key={f.key}
                      description={f.description}
                      on={valueOf(f.key, f.inTrial)}
                      dirty={draft[f.key] !== undefined && draft[f.key] !== f.inTrial}
                      onToggle={() => handleToggle(f.key, f.inTrial)}
                    />
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {mutation.isError && (
        <div className="mt-3 rounded-[10px] bg-destructive/10 px-3.5 py-2.5 text-[12.5px] font-medium text-destructive">
          Не удалось сохранить. Попробуйте ещё раз
        </div>
      )}
    </TariffSection>
  );
};

const FeatureRow = ({
  description,
  on,
  dirty,
  onToggle,
}: {
  description: string;
  on: boolean;
  dirty: boolean;
  onToggle: () => void;
}) => (
  <button
    role="switch"
    aria-checked={on}
    onClick={onToggle}
    className="flex w-full items-center justify-between gap-3 border-b border-skr-hairline px-3.5 py-[10px] text-left last:border-b-0 hover:bg-skr-head"
  >
    <span className={cn('text-[13px]', on ? 'text-foreground' : 'text-muted-foreground')}>
      {description}
      {dirty && <span className="ml-1.5 align-middle text-primary">•</span>}
    </span>
    <span
      className={cn(
        'relative h-[22px] w-[38px] shrink-0 rounded-full transition-colors',
        on ? 'bg-primary' : 'bg-skr-line2',
      )}
    >
      <span
        className={cn(
          'absolute top-[3px] size-4 rounded-full bg-card shadow transition-transform',
          on ? 'translate-x-[19px]' : 'translate-x-[3px]',
        )}
      />
    </span>
  </button>
);
